import { BlockLoading } from '../fetch-status/block-loading';
import { Separator } from '../ui/separator';
import {
  ViewDisputeIcon,
  ViewFinalIcon,
  ViewProposedIcon,
  ViewStatusDisputedIconUrl,
  ViewStatusEndedIconUrl,
  ViewStatusReleasedIconUrl,
} from '@/client/assets';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';
import { MarketStatus } from '@/libs/sdk/api';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Large fixed-colour illustrations (~20KB each). Rendered as <img> from emitted
// files instead of ?react components so their path data is not inlined into the
// app entry chunk. width/height carry the 159x149 viewBox ratio so the box is
// reserved before the file loads; h-full/w-auto keep the same rendered size.
const STATUS_ILLUSTRATION: Partial<Record<MarketStatus, string>> = {
  [MarketStatus.settlement]: ViewStatusReleasedIconUrl,
  [MarketStatus.disputed]: ViewStatusDisputedIconUrl,
  [MarketStatus.closed]: ViewStatusEndedIconUrl,
};

type MarketStatusViewProps = {
  className?: string;
  marketAddress: string;
};
export const MarketStatusView = ({ className, marketAddress }: MarketStatusViewProps) => {
  const { t } = useTranslation();
  const market = useMarketDetail(api, { marketAddress });
  const resultOption = useMemo(
    () =>
      market.data?.optionList.find(
        item => item.optionAddress.toLowerCase() === market.data.resultOptionAddress.toLowerCase()
      ),
    [market.data]
  );
  if (!market.data || market.isLoading) return <BlockLoading />;
  return (
    <div className={cn('flex items-center justify-between p-4 h-[9.25rem] rounded-3', className)}>
      <div className="text-foreground/70 text-sm  font-medium leading-none flex flex-col h-full items-start flex-1">
        <div className="flex space-x-1 items-center">
          <ViewProposedIcon
            className={cn(
              'size-4',
              market.data?.marketStatus >= MarketStatus.settlement ? 'fill-beneficial' : 'fill-foreground/40'
            )}
          />
          <span
            className={cn('whitespace-nowrap', market.data?.marketStatus >= MarketStatus.settlement && 'text-foreground')}
          >
            {t('outcome-proposed')}
          </span>
        </div>
        <div className="flex-1 flex space-x-3 pl-2">
          <Separator
            orientation="vertical"
            className={cn('h-full', market.data?.marketStatus >= MarketStatus.settlement && 'bg-beneficial')}
          />
          {resultOption && (
            <span className="mt-1 text-beneficial">{resultOption.description ?? resultOption?.optionName}</span>
          )}
        </div>
        <div className="flex space-x-1 items-center">
          <ViewDisputeIcon
            className={cn(
              'size-4 ',
              market.data?.marketStatus == MarketStatus.disputed && 'animate-spin',
              market.data?.marketStatus >= MarketStatus.disputed ? 'fill-beneficial' : 'fill-foreground/40'
            )}
          />
          <span className={cn(market.data?.marketStatus >= MarketStatus.disputed && 'text-foreground')}>
            {t('dispute-window')}
          </span>
        </div>
        <div className="flex-1 flex space-x-3 pl-2">
          <Separator
            orientation="vertical"
            className={cn('h-full', market.data?.marketStatus > MarketStatus.disputed && 'bg-beneficial')}
          />
          <span></span>
        </div>
        <div className="flex space-x-1 items-center">
          <ViewFinalIcon
            className={cn(
              'size-4',
              market.data?.marketStatus >= MarketStatus.closed ? 'fill-beneficial' : 'fill-foreground/40'
            )}
          />
          <div className="flex flex-col">
            <span className={cn(market.data?.marketStatus >= MarketStatus.closed && 'text-foreground')}>
              {t('final-outcome')}
            </span>
            {resultOption && market.data?.marketStatus >= MarketStatus.closed && (
              <span className="mt-1 text-beneficial">{resultOption.description ?? resultOption?.optionName}</span>
            )}
          </div>
        </div>
      </div>
      <div className="h-full">
        {STATUS_ILLUSTRATION[market.data.marketStatus] && (
          <img
            src={STATUS_ILLUSTRATION[market.data.marketStatus]}
            alt=""
            aria-hidden="true"
            width={159}
            height={149}
            decoding="async"
            className="h-full w-auto"
          />
        )}
      </div>
    </div>
  );
};
